<template>
  <div class="br-tab-stock-inventory">
    <section>
      <KpiCards class="mb-3" :items="kpiItems" :loading="summaryLoading" />

      <v-alert v-if="summaryError" type="error" text class="mb-3">
        {{ $t("stock_report_summary_failed") }}
      </v-alert>

      <!-- Main report panel -->
      <v-card class="br-panel mb-3" flat>
        <v-card-text class="pb-2 pt-3">
          <!-- Panel header: title + tabs -->
          <div class="d-flex align-center flex-wrap br-panel-header">
            <span class="br-accent-bar" />
            <h1 class="br-panel-title">{{ $t("stock_report") }}</h1>
            <span class="br-panel-count">{{ formatNumber(total) }} {{ $t("rows") }}</span>
            <v-spacer />

            <!-- View switch tabs -->
            <div class="br-tabs mr-2">
              <div
                class="br-tab"
                :class="{ 'br-tab--active': view === 'flat' }"
                @click="view = 'flat'"
              >
                <v-icon size="12" class="mr-1" :color="view === 'flat' ? 'primary' : 'grey'">fal fa-list</v-icon>
                {{ $t("flat_list") }}
              </div>
              <div
                class="br-tab"
                :class="{ 'br-tab--active': view === 'group' }"
                @click="view = 'group'"
              >
                <v-icon size="12" class="mr-1" :color="view === 'group' ? 'primary' : 'grey'">fal fa-layer-group</v-icon>
                {{ $t("group_by_product") }}
              </div>
            </div>

            <!-- Type tabs -->
            <div class="br-tabs">
              <div
                v-for="t in types"
                :key="t"
                class="br-tab"
                :class="{ 'br-tab--active': filters.type === t }"
                @click="filters.type = t"
              >
                {{ $t(t) }}
              </div>
            </div>
          </div>

          <!-- Filter row -->
          <div class="d-flex align-center flex-wrap br-filter-row mt-3">
            <TextSearch
              v-model="search"
              class="br-search"
              placeholder="stock_report_search_hint"
              @search="applySearch"
              @onClear="clearSearch"
            />

            <TextSelect
              v-model="filters.category_id"
              label="category"
              class="br-select"
              dense
              :placeholder="false"
              searchable
              :disabled="filters.type === 'ingredient'"
              :items="categoryOptions"
              itemValue="id"
              text="name"
              :returnObject="false"
              hide-details
            />

            <TextSelect
              v-model="filters.vendor_id"
              label="supplier"
              class="br-select"
              dense
              :placeholder="false"
              searchable
              :items="supplierOptions"
              itemValue="id"
              text="name"
              :returnObject="false"
              hide-details
            />

            <TextSelect
              v-model="filters.status"
              label="stock_status"
              class="br-select"
              dense
              :placeholder="false"
              :items="statusOptions"
              itemValue="value"
              text="text"
              :returnObject="false"
              hide-details
            />

            <TextSelect
              v-model="filters.sort"
              label="sort_by"
              class="br-select"
              dense
              :placeholder="false"
              :items="sortOptions"
              itemValue="value"
              text="text"
              :returnObject="false"
              hide-details
            />

            <span v-if="isFiltered" class="br-clear-link" @click="clearFilters">
              {{ $t("clear_filters") }}
            </span>

            <v-spacer />

            <div class="br-export-btn" @click="handleExcelExport">
              <v-progress-circular
                v-if="exporting"
                size="13"
                width="2"
                indeterminate
                color="primary"
                class="mr-1"
              />
              <v-icon v-else size="13" class="mr-1">fal fa-file-export</v-icon>
              {{ $t("export") }}
            </div>
          </div>

          <v-alert v-if="lookupError" type="error" text class="mt-3 mb-0">
            {{ $t("stock_report_filters_failed") }}
          </v-alert>

          <!-- Table -->
          <div class="br-table-wrap mt-3">
            <v-alert v-if="tableError" type="error" text class="ma-3">
              {{ $t("stock_report_load_failed") }}
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="items"
              :server-items-length="total"
              :page="page"
              :items-per-page="perPage"
              :loading="loading"
              :loading-text="$t('loading')"
              :no-data-text="$t('no_data')"
              :footer-props="{
                'items-per-page-options': rowsPerPageItems,
                'items-per-page-text': $t('rows_per_page'),
              }"
              :hide-default-footer="tableError || isPrinting"
              class="br-data-table elevation-0"
              disable-sort
              @update:page="changePage"
              @update:items-per-page="changePerPage"
            >
              <template v-slot:body>
                <tbody v-if="!loading && !tableError && items.length">
                  <template v-for="entry in displayRows">
                    <tr
                      :key="entry.key"
                      :class="{ 'stock-group-header': entry.header }"
                    >
                      <td class="text-center text-caption grey--text">
                        {{
                          entry.header ? "" : (page - 1) * perPage + entry.index + 1
                        }}
                      </td>
                      <td>
                        <div v-if="!entry.child" class="d-flex align-center py-1">
                          <v-img
                            :src="productImage(entry.item.image)"
                            contain
                            aspect-ratio="1"
                            width="40"
                            height="40"
                            max-width="40"
                            max-height="40"
                            min-width="40"
                            min-height="40"
                            class="stock-thumbnail rounded mr-2"
                          >
                            <template v-slot:placeholder>
                              <div class="stock-image-placeholder">
                                <v-icon size="24" color="grey lighten-1">fal fa-image</v-icon>
                              </div>
                            </template>
                          </v-img>
                          <div>
                            <div class="font-weight-medium text-body-2">
                              {{ productName(entry.item) }}
                            </div>
                            <div class="text-caption grey--text">
                              {{ entry.item.code }}
                              <template v-if="entry.item.category_name">
                                · {{ entry.item.category_name }}
                              </template>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span v-if="entry.header" class="text-caption font-weight-medium primary--text">
                          {{ $t("subtotal") }} · {{ entry.count }} {{ $t("variant_s") }}
                        </span>
                        <span
                          v-else
                          :class="{ 'grey--text text-caption': !hasVariant(entry.item) }"
                          class="text-body-2"
                        >
                          {{
                            hasVariant(entry.item)
                              ? entry.item.variant_name
                              : $t("no_variant")
                          }}
                        </span>
                      </td>
                      <td class="stock-code">
                        <template v-if="!entry.header">
                          <div class="text-body-2 font-numeric">{{ entry.item.sku || "—" }}</div>
                          <small class="text-caption grey--text font-numeric">
                            {{ entry.item.variant_barcode || entry.item.barcode || "—" }}
                          </small>
                        </template>
                      </td>
                      <td>
                        <span class="text-body-2">
                          {{ entry.header ? "" : entry.item.unit_name || "—" }}
                        </span>
                      </td>
                      <td class="text-right">
                        <span
                          class="font-weight-bold font-numeric text-body-2"
                          :class="Number(entry.stock) === 0 ? 'red--text' : ''"
                        >
                          {{ formatNumber(entry.stock) }}
                        </span>
                        <div v-if="!entry.header" class="text-caption grey--text font-numeric">
                          {{ $t("min_stock") }} {{ formatNumber(entry.item.min_stock) }}
                        </div>
                      </td>
                      <td>
                        <span
                          v-if="!entry.header"
                          class="br-tag"
                          :class="statusTagClass(entry.item.stock_status)"
                        >
                          {{ $t(entry.item.stock_status) }}
                        </span>
                      </td>
                      <td class="text-right font-weight-medium font-numeric text-body-2">
                        {{ formatNumber(entry.value) }}
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td :colspan="headers.length" class="text-center py-12">
                      <template v-if="loading">
                        <v-progress-circular indeterminate color="primary" size="28" />
                        <div class="mt-2 text-caption grey--text">{{ $t("loading") }}</div>
                      </template>
                      <template v-else-if="!tableError">
                        <v-icon size="56" color="grey lighten-1">fal fa-box-open</v-icon>
                        <div class="mt-3 text-subtitle-1 grey--text text--darken-2 font-weight-medium">
                          {{ $t("no_data") }}
                        </div>
                        <small class="grey--text">{{ $t("no_data_hint") }}</small>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </section>
  </div>
</template>

<script>
import TextSelect from "@/components/global/TextSelect";
import TextSearch from "@/components/global/TextSearch";
import { myMixin } from "@/mixin/main";
import { error, toast } from "@/controllers/alert";
import {
  reportTable,
  reportSummary,
  groupStockRows,
} from "@/utils/stockReport";

const defaults = () => ({
  type: "all",
  category_id: "",
  vendor_id: "",
  status: "all",
  sort: "name",
  q: "",
});

export default {
  name: "StockReportPage",
  components: {
    TextSelect,
    TextSearch,
  },
  data: () => ({
    filters: defaults(),
    search: "",
    searchTimer: null,
    view: "flat",
    isPrinting: false,
    categories: [],
    suppliers: [],
    items: [],
    summary: null,
    total: 0,
    page: 1,
    perPage: 25,
    loading: false,
    summaryLoading: false,
    exporting: false,
    tableError: false,
    summaryError: false,
    lookupError: false,
    tableRequest: 0,
    summaryRequest: 0,
    types: ["all", "product", "variant", "ingredient"],
  }),
  computed: {
    rowsPerPageItems() {
      return this.$store.getters["index/rowsPerPageItems"] || [15, 30, 50];
    },
    kpiItems() {
      return this.cards.map((card) => ({
        key: card.key,
        title: card.label,
        icon: card.icon,
        value: this.summary ? this.formatNumber(this.summary[card.key]) : "—",
        note: this.$t(card.sub),
      }));
    },
    grouped() {
      return this.isPrinting || this.view === "group";
    },
    isFiltered() {
      return (
        this.search !== "" ||
        this.filters.category_id !== "" ||
        this.filters.vendor_id !== "" ||
        this.filters.status !== "all" ||
        this.filters.sort !== "name" ||
        this.filters.type !== "all"
      );
    },
    filterQuery() {
      return JSON.stringify({
        ...this.filters,
        category_id:
          this.filters.type === "ingredient" ? "" : this.filters.category_id,
      });
    },
    categoryOptions() {
      const key =
        { en: "en_name", th: "th_name", cn: "cn_name", ch: "cn_name" }[
          this.$i18n.locale
        ] || "la_name";
      return [
        { id: "", name: this.$t("all") },
        ...this.categories.map((c) => ({
          id: c.id,
          name: c[key] || c.la_name || String(c.id),
        })),
      ];
    },
    supplierOptions() {
      return [{ id: "", name: this.$t("all") }, ...this.suppliers];
    },
    statusOptions() {
      return this.options([
        ["all", "all"],
        ["in", "in_stock"],
        ["low", "low_stock"],
        ["out", "out_of_stock"],
      ]);
    },
    sortOptions() {
      return this.options([
        ["name", "product_name"],
        ["stock_asc", "stock_low_high"],
        ["stock_desc", "stock_high_low"],
        ["value_desc", "value_high_low"],
        ["code", "code"],
      ]);
    },
    cards() {
      return [
        {
          key: "total_skus",
          label: "total_skus",
          sub: "rows",
          icon: "fal fa-boxes",
          color: "primary",
        },
        {
          key: "total_qty",
          label: "total_qty",
          sub: "units_on_hand",
          icon: "fal fa-cubes",
          color: "primary",
        },
        {
          key: "total_value",
          label: "stock_value",
          sub: "at_cost",
          icon: "fal fa-coins",
          color: "primary",
        },
        {
          key: "low_count",
          label: "low_stock",
          sub: "low_stock_items",
          icon: "fal fa-exclamation-triangle",
          color: "primary",
        },
        {
          key: "out_count",
          label: "out_of_stock",
          sub: "out_of_stock_items",
          icon: "fal fa-times-circle",
          color: "primary",
        },
      ];
    },
    headers() {
      return [
        "no",
        "product_name",
        "option",
        "sku",
        "unit",
        "stock",
        "stock_status",
        "stock_value",
      ].map((key, index) => ({
        text:
          index === 3
            ? `${this.$t("sku")} / ${this.$t("barcode")}`
            : index === 2
            ? `${this.$t("option")} / ${this.$t("variant")}`
            : this.$t(key),
        value: key,
        sortable: false,
        align: index === 0 ? "center" : [5, 7].includes(index) ? "end" : "start",
      }));
    },
    displayRows() {
      if (!this.grouped)
        return this.items.map((item, index) => ({
          key: `row:${index}`,
          item,
          index,
          stock: item.stock,
          value: item.stock_value,
        }));
      return groupStockRows(this.items).flatMap((group) => [
        ...(group.variant
          ? [
              {
                key: group.key,
                header: true,
                item: group.product,
                stock: group.stock,
                value: group.value,
                count: group.rows.length,
              },
            ]
          : []),
        ...group.rows.map((item) => ({
          key: `row:${item.rowNumber}`,
          item,
          index: item.rowNumber,
          child: group.variant,
          stock: item.stock,
          value: item.stock_value,
        })),
      ]);
    },
  },
  watch: {
    filterQuery() {
      if (this.filters.type === "ingredient") this.filters.category_id = "";
      this.page = 1;
      this.fetchItems();
      this.fetchSummary();
    },
  },
  created() {
    this.fetchLookups();
    this.fetchItems();
    this.fetchSummary();
  },
  mounted() {
    window.addEventListener("beforeprint", this.beforePrint);
    window.addEventListener("afterprint", this.afterPrint);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.tableRequest++;
    this.summaryRequest++;
    window.removeEventListener("beforeprint", this.beforePrint);
    window.removeEventListener("afterprint", this.afterPrint);
    document.body.classList.remove("printing-stock-report");
  },
  methods: {
    productImage: myMixin.methods.IMAGES,
    formatNumber(value) {
      return value == null ? "—" : myMixin.filters.NumberFormat(value);
    },
    options(entries) {
      return entries.map(([value, label]) => ({ value, text: this.$t(label) }));
    },
    hasVariant(item) {
      return [true, 1, "1"].includes(item.has_variant);
    },
    productName(item) {
      return this.$i18n.locale === "la"
        ? item.product_lo || item.product_name
        : item.product_name || item.product_lo;
    },
    statusTagClass(status) {
      return {
        in_stock: "br-tag--success",
        low_stock: "br-tag--warning",
        out_of_stock: "br-tag--error",
      }[status] || "br-tag--neutral";
    },
    params() {
      return JSON.parse(this.filterQuery);
    },
    applySearch() {
      clearTimeout(this.searchTimer);
      this.filters.q = this.search;
    },
    clearSearch() {
      this.search = "";
      this.applySearch();
    },
    clearFilters() {
      clearTimeout(this.searchTimer);
      this.search = "";
      this.page = 1;
      if (this.filterQuery === JSON.stringify(defaults())) {
        this.fetchItems();
        this.fetchSummary();
      } else {
        this.filters = defaults();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchItems();
    },
    changePerPage(size) {
      if (size === this.perPage) return;
      this.perPage = size;
      this.page = 1;
      this.fetchItems();
    },
    async fetchItems() {
      const request = ++this.tableRequest;
      this.loading = true;
      this.tableError = false;
      this.items = [];
      try {
        const { data } = await this.$http.get("stock_report", {
          params: { ...this.params(), page: this.page, per_page: this.perPage },
          skipErrorAlert: true,
        });
        if (request !== this.tableRequest) return;
        const payload = reportTable(data);
        this.items = payload.data;
        this.total = Number(payload.total);
        this.perPage = Number(payload.per_page);
      } catch (err) {
        if (request !== this.tableRequest) return;
        this.tableError = true;
        this.total = 0;
        console.error("stock_report", err);
        error(this, "stock_report_load_failed");
      } finally {
        if (request === this.tableRequest) this.loading = false;
      }
    },
    async fetchSummary() {
      const request = ++this.summaryRequest;
      this.summaryLoading = true;
      this.summaryError = false;
      this.summary = null;
      try {
        const { data } = await this.$http.get("get_summary_stock_report", {
          params: this.params(),
          skipErrorAlert: true,
        });
        if (request !== this.summaryRequest) return;
        this.summary = reportSummary(data);
      } catch (err) {
        if (request !== this.summaryRequest) return;
        this.summaryError = true;
        console.error("get_summary_stock_report", err);
        error(this, "stock_report_summary_failed");
      } finally {
        if (request === this.summaryRequest) this.summaryLoading = false;
      }
    },
    async fetchLookups() {
      await Promise.all(
        ["categories", "suppliers"].map(async (endpoint) => {
          try {
            const { data } = await this.$http.get(endpoint, {
              skipErrorAlert: true,
            });
            if (data.status === false || !Array.isArray(data.data)) {
              throw new Error(`${endpoint}: expected data array`);
            }
            this[endpoint] = data.data;
          } catch (err) {
            this.lookupError = true;
            console.error(endpoint, err);
            error(this, "stock_report_filters_failed");
          }
        })
      );
    },
    async handleExcelExport() {
      if (this.exporting) return;
      this.applySearch();
      this.exporting = true;
      let blobUrl;
      try {
        const { data } = await this.$http.get("download_stock_report", {
          params: { ...this.params(), locale: this.$i18n.locale },
          responseType: "blob",
          skipErrorAlert: true,
        });
        if (
          !(data instanceof Blob) ||
          !data.size ||
          /json|text\/html/.test(data.type)
        ) {
          throw new Error("download_stock_report: expected XLSX blob");
        }
        blobUrl = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `stock_report_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast(this, "excel_export_success");
      } catch (err) {
        console.error("download_stock_report", err);
        error(this, "excel_export_failed");
      } finally {
        if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        this.exporting = false;
      }
    },
    beforePrint() {
      this.isPrinting = true;
      document.body.classList.add("printing-stock-report");
    },
    afterPrint() {
      this.isPrinting = false;
      document.body.classList.remove("printing-stock-report");
    },
  },
};
</script>

<style scoped>
.br-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-accent-soft: rgba(91, 57, 201, 0.12);
  --br-accent-softer: rgba(91, 57, 201, 0.07);
  --br-accent-border: rgba(91, 57, 201, 0.35);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.br-page--dark {
  --br-text: #ffffff;
  --br-border: rgba(255, 255, 255, 0.08);
  background: #1b1d30;
}

.br-page--dark .br-panel,
.br-page--dark .br-panel.v-card.v-sheet {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.br-page section {
  width: 100%;
}

/* Main panel */
.br-panel,
.br-panel.v-card.v-sheet {
  background: #ffffff !important;
  border: 1px solid var(--br-border) !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}
.br-panel-header {
  gap: 10px;
}
.br-accent-bar {
  width: 3px;
  height: 15px;
  background: var(--br-accent);
  border-radius: 2px;
}
.br-panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: var(--br-text);
}
.br-panel-count {
  font-size: 11.5px;
  color: var(--br-muted);
}
.br-tabs {
  display: flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid var(--br-border);
}
.br-tab {
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: var(--br-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}
.br-page--dark .br-tab--active {
  color: #c4b5fd !important;
  background: rgba(154, 140, 240, 0.18) !important;
  box-shadow: none;
}

/* Filter row */
.br-filter-row {
  gap: 10px;
}
.br-search {
  flex: 1 1 220px;
  min-width: 210px;
  max-width: 300px;
}
.br-select {
  max-width: 170px;
}
.br-clear-link {
  font-size: 12px;
  color: var(--br-accent);
  cursor: pointer;
  margin-left: 4px;
}
.br-clear-link:hover {
  text-decoration: underline;
}
.br-export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: var(--br-radius-md);
  border: 1px solid var(--br-border);
  color: var(--br-muted);
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  user-select: none;
}
.br-export-btn:hover {
  color: var(--br-text);
  border-color: #cbd5e1;
}

/* Tags */
.br-tag {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid transparent;
}
.br-tag--success {
  color: #1b7a3d;
  background: #e3f7ea;
  border-color: #bdeccb;
}
.br-tag--warning {
  color: #9a6700;
  background: #fff3d6;
  border-color: #f3d896;
}
.br-tag--error {
  color: #b3261e;
  background: #fdecea;
  border-color: #f6c6c2;
}
.br-tag--neutral {
  color: var(--br-muted);
  background: #f1f2f7;
  border-color: var(--br-border);
}

/* Table styling */
.br-table-wrap ::v-deep .v-data-table {
  background: transparent !important;
}
.br-table-wrap ::v-deep .v-data-table-header th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
  color: var(--br-muted) !important;
  border-bottom: 1px solid var(--br-border) !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}
.br-table-wrap ::v-deep .v-data-table tbody tr {
  border-bottom: 1px solid #f1f2f7 !important;
}
.br-table-wrap ::v-deep .v-data-table tbody tr:hover {
  background: var(--br-accent-softer) !important;
}
.stock-group-header {
  background: #f8fafc;
}
.stock-thumbnail {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  flex: 0 0 40px !important;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  overflow: hidden;
}
.stock-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f1f5f9;
}
.font-numeric {
  font-variant-numeric: tabular-nums;
}

.br-page--dark .br-table-wrap ::v-deep .v-data-table-header th {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}
.br-page--dark .br-table-wrap ::v-deep .v-data-table tbody tr {
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}
.br-page--dark .stock-group-header {
  background: #262638;
}
.br-page--dark .stock-thumbnail,
.br-page--dark .stock-image-placeholder,
.br-page--dark .br-tag--neutral {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.1);
}
</style>
